import { useState } from "react";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  MessageCircle,
  Mail,
  Twitter,
  Facebook,
  Gift,
  CheckCircle2
} from "lucide-react";
import { Button } from "./ui/button";

const InviteShareCard = ({ inviteCode, userName = "A golfer" }) => {
  const [copied, setCopied] = useState(false);
  
  // Use window.location.origin for proper environment URLs (preview vs production)
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://parseeker.com';
  const inviteUrl = `${baseUrl}/join?ref=${inviteCode}`;
  const shareMessage = `Join me on Par Seeker! Track your golf scores, find playing partners, and compete in tournaments. Use my invite code: ${inviteCode}`;

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        toast.success("Invite link copied!");
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = inviteUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        toast.success("Invite link copied!");
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // Show the code so user can manually copy
      toast.info(`Your invite code: ${inviteCode}`);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Par Seeker",
          text: shareMessage,
          url: inviteUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      handleCopy();
    }
  };

  const shareLinks = [
    {
      name: "Messages",
      icon: MessageCircle,
      color: "bg-emerald-500",
      url: `sms:?body=${encodeURIComponent(shareMessage + "\n" + inviteUrl)}`
    },
    {
      name: "Email",
      icon: Mail,
      color: "bg-blue-500",
      url: `mailto:?subject=${encodeURIComponent("Join me on Par Seeker!")}&body=${encodeURIComponent(shareMessage + "\n\n" + inviteUrl)}`
    },
    {
      name: "Twitter",
      icon: Twitter,
      color: "bg-sky-500",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(inviteUrl)}`
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-blue-600",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteUrl)}&quote=${encodeURIComponent(shareMessage)}`
    }
  ];

  return (
    <div data-testid="invite-share-card" className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/10 border border-emerald-500/30 rounded-2xl p-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
          <Gift className="w-6 h-6 text-emerald-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Invite Friends</h3>
          <p className="text-emerald-400/70 text-sm">Earn 50 coins per verified referral!</p>
        </div>
      </div>

      {/* Invite Code Display */}
      <div className="bg-black/30 rounded-xl p-4 mb-4">
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-1">Your Invite Code</p>
          <p data-testid="invite-code-value" className="text-2xl font-mono font-bold text-white tracking-wider">{inviteCode}</p>
        </div>
      </div>

      {/* Copy Link Button */}
      <Button
        data-testid="invite-copy-link-btn"
        onClick={handleCopy}
        className={`w-full mb-4 ${
          copied 
            ? 'bg-emerald-500 hover:bg-emerald-600' 
            : 'bg-gray-800 hover:bg-gray-700'
        } transition-colors`}
      >
        {copied ? (
          <>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 mr-2" />
            Copy Invite Link
          </>
        )}
      </Button>

      {/* Share Buttons */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {shareLinks.map((link) => {
          const Icon = link.icon;
          return (
            <a
              key={link.name}
              data-testid={`invite-share-${link.name.toLowerCase()}`}
              href={link.url}
              target={link.name === "Messages" ? "_self" : "_blank"}
              rel="noopener noreferrer"
              className={`${link.color} rounded-xl p-3 flex flex-col items-center justify-center hover:opacity-90 transition-opacity`}
            >
              <Icon className="w-5 h-5 text-white mb-1" />
              <span className="text-white text-xs">{link.name}</span>
            </a>
          );
        })}
      </div>

      {/* Native Share (if available) */}
      {typeof navigator !== 'undefined' && navigator.share && (
        <Button
          onClick={handleShare}
          variant="outline"
          className="w-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
        >
          <Share2 className="w-4 h-4 mr-2" />
          More Sharing Options
        </Button>
      )}

      {/* Reward Info */}
      <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <p className="text-amber-400 text-sm text-center">
          <span className="font-semibold">+50 coins</span> when your friend logs their first round!
        </p>
      </div>
    </div>
  );
};

export default InviteShareCard;
